import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Button, Form, Container, Row, Col, Spinner, Badge, Pagination, Modal, Card, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsCheckCircleFill, BsXCircleFill, BsPersonSlash, BsArrowCounterclockwise, BsInfoCircle } from 'react-icons/bs';
import { FaUserSlash, FaUserAlt, FaUserClock } from 'react-icons/fa';
import { BiError } from 'react-icons/bi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../../api';
import { ProcessoViewModel } from '../../../viewmodel/ProcessoViewModel';
import Processo from '../../../model/processo/Processo';

interface CandidatoResponsavel {
    id: string;
    nome: string;
    nomeSocial: string;
    cpf: string;
    passaporte: string;
    email: string;
}

// NOVA INTERFACE para múltiplas inscrições
interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoOnlyResponse {
    id: string;
    // REMOVER: inscricao: string; (substituído por candidatoEditais)
    candidatoEditais: CandidatoEdital[]; // NOVO CAMPO para múltiplas inscrições
    nome: string;
    nomeSocial: string;
    cpf: string;
    passaporte: string;
    email: string;
    status: string;
    autodeclaracaoRacial?: string | null;
    candidatoResponsavel: CandidatoResponsavel | null;
    temFoto: boolean;
    validacaoAnterior?: {
        candidatoId: string;
        processoNome: string;
        processoIdentificador: string;
        edital: string;
        bancaId: string | null;
        tipoBanca: string | null;
        statusVotacao: string | null;
        resultadoVotacao: string | null;
        dataVotacao: string | null;
    } | null;
    duplicidade: boolean;
}

enum StatusCandidato {
    NAO_AVALIADO = "NAO_AVALIADO",
    INCONCLUSIVO = "INCONCLUSIVO",
    VALIDADO = "VALIDADO",
    VALIDADO_ANTERIORMENTE = "VALIDADO_ANTERIORMENTE",
    NAO_VALIDADO = "NAO_VALIDADO",
    DELETADO = "DELETADO",
    AUSENTE = "AUSENTE"
}

interface PaginatedResponse<T> {
    content: T[];
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    empty: boolean;
}

const CandidatosProcesso: React.FC = () => {
    const { processoId } = useParams<{ processoId: string }>();
    const [candidatos, setCandidatos] = useState<CandidatoOnlyResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [exibirDeletados, setExibirDeletados] = useState<boolean>(false);
    const [filtroNome, setFiltroNome] = useState<string>('');
    const [filtroInscricao, setFiltroInscricao] = useState<string>('');
    const [filtroCpf, setFiltroCpf] = useState<string>('');
    const [filtroPassaporte, setFiltroPassaporte] = useState<string>('');
    const [filtroEmail, setFiltroEmail] = useState<string>('');
    const [filtroStatus, setFiltroStatus] = useState<StatusCandidato | 'TODOS'>('TODOS');
    const [paginaAtual, setPaginaAtual] = useState<number>(0);
    const [tamanhoPagina, setTamanhoPagina] = useState<number>(20);
    const [ordenacao, setOrdenacao] = useState<string>('nome');
    const [paginationData, setPaginationData] = useState<PaginatedResponse<CandidatoOnlyResponse> | null>(null);
    const [showResponsavelModal, setShowResponsavelModal] = useState<boolean>(false);
    const [responsavelSelecionado, setResponsavelSelecionado] = useState<CandidatoResponsavel | null>(null);
    const [processo, setProcesso] = useState<Processo | null>(null);
    const [estatisticas, setEstatisticas] = useState<Record<string, number>>({});
    const [estatisticasEstaticas, setEstatisticasEstaticas] = useState<Record<string, number>>({});
    const [debouncing, setDebouncing] = useState(false);

    // Cores de fundo para cada status (tons claros)
    const statusColors = {
        [StatusCandidato.VALIDADO]: '#e6f7ee', // Verde muito claro
        [StatusCandidato.VALIDADO_ANTERIORMENTE]: '#dff2f7', // Azul-esverdeado claro
        [StatusCandidato.NAO_VALIDADO]: '#fde8e8', // Vermelho muito claro
        [StatusCandidato.NAO_AVALIADO]: '#e6f7fb', // Azul muito claro
        [StatusCandidato.INCONCLUSIVO]: '#fff8e6', // Amarelo muito claro
        [StatusCandidato.AUSENTE]: '#f8f9fa', // Cinza muito claro
        [StatusCandidato.DELETADO]: '#f3e8ff', // Roxo muito claro
    };

    // NOVA FUNÇÃO: Renderizar múltiplas inscrições
    const renderInscricoes = (candidatoEditais: CandidatoEdital[]) => {
        if (candidatoEditais.length === 0) {
            return '-';
        } else if (candidatoEditais.length === 1) {
            return (
                <div>
                    {candidatoEditais[0].inscricao} - {candidatoEditais[0].nomeEdital}
                </div>
            );
        } else {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                            {candidatoEditais.map((edital, index) => (
                                <div key={edital.id}>
                                    {edital.inscricao} - {edital.nomeEdital}
                                    {index < candidatoEditais.length - 1 && <br />}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <Badge bg="secondary" className="cursor-pointer">
                        {candidatoEditais.length} inscrições
                    </Badge>
                </OverlayTrigger>
            );
        }
    };

    const getStatusIcon = (status: string): JSX.Element => {
        switch (status) {
            case StatusCandidato.VALIDADO:
                return <BsCheckCircleFill color="#28a745" size={23} />;
            case StatusCandidato.NAO_VALIDADO:
                return <BsXCircleFill color="#dc3545" size={23} />;
            case StatusCandidato.VALIDADO_ANTERIORMENTE:
                return <BsCheckCircleFill color="#0d6efd" size={23} />;
            case StatusCandidato.NAO_AVALIADO:
                return <FaUserAlt color="#17a2b8" size={23} />;
            case StatusCandidato.INCONCLUSIVO:
                return <FaUserClock color="#ffc107" size={23} />;
            case StatusCandidato.AUSENTE:
                return <FaUserSlash color="#6c757d" size={23} />;
            case StatusCandidato.DELETADO:
                return <BsPersonSlash color="#6f42c1" size={23} />;
            default:
                return <BiError color="#ffc107" size={23} />;
        }
    };

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case StatusCandidato.VALIDADO:
                return 'Validado';
            case StatusCandidato.NAO_VALIDADO:
                return 'Não Validado';
            case StatusCandidato.VALIDADO_ANTERIORMENTE:
                return 'Validado anteriormente';
            case StatusCandidato.NAO_AVALIADO:
                return 'Não avaliado';
            case StatusCandidato.INCONCLUSIVO:
                return 'Inconclusivo';
            case StatusCandidato.AUSENTE:
                return 'Ausente';
            case StatusCandidato.DELETADO:
                return 'Deletado';
            default:
                return 'Desconhecido';
        }
    };

    const fetchCandidatos = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                exibirDeletados,
                page: paginaAtual,
                size: tamanhoPagina,
                sort: ordenacao,
                nome: debouncedFiltros.nome || undefined,
                inscricao: debouncedFiltros.inscricao || undefined,
                cpf: debouncedFiltros.cpf || undefined,
                passaporte: debouncedFiltros.passaporte || undefined,
                email: debouncedFiltros.email || undefined,
                status: filtroStatus !== 'TODOS' ? filtroStatus : undefined
            };
            
            console.log('Parâmetros da requisição:', params);
            
            const response = await api.get(`/processos/${processoId}/candidatos/estatisticas`, { params });
            
            setCandidatos(response.data.candidatos.content);
            setPaginationData(response.data.candidatos);
            
            // Atualizar estatísticas estáticas apenas se ainda não foram carregadas
            if (Object.keys(estatisticasEstaticas).length === 0) {
                const stats = {
                    TOTAL: response.data.estatisticas.total,
                    [StatusCandidato.VALIDADO]: response.data.estatisticas.validados,
                    [StatusCandidato.VALIDADO_ANTERIORMENTE]: 0,
                    [StatusCandidato.NAO_VALIDADO]: response.data.estatisticas.naoValidados,
                    [StatusCandidato.AUSENTE]: response.data.estatisticas.ausentes,
                    [StatusCandidato.INCONCLUSIVO]: response.data.estatisticas.inconclusivos,
                    [StatusCandidato.NAO_AVALIADO]: response.data.estatisticas.indefinidos
                };
                setEstatisticasEstaticas(stats);
            }
        } catch (err) {
            console.error('Erro ao carregar candidatos:', err);
            setError('Erro ao carregar candidatos. Tente novamente mais tarde.');
            toast.error('Erro ao carregar candidatos!');
        } finally {
            setLoading(false);
        }
    };

    const fetchProcesso = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await ProcessoViewModel.obterProcessoPorId(processoId!);
            setProcesso(response);

        } catch (err) {
            console.error('Erro ao carregar Processo:', err);
        } finally {
            setLoading(false);
        }
    }

    // Debounce para filtros de texto
    const debounceTimer = useRef<NodeJS.Timeout>();
    const [debouncedFiltros, setDebouncedFiltros] = useState({
        nome: '',
        inscricao: '',
        cpf: '',
        passaporte: '',
        email: ''
    });
    
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        if (filtroNome || filtroInscricao || filtroCpf || filtroPassaporte || filtroEmail) {
            setDebouncing(true);
        }
        
        debounceTimer.current = setTimeout(() => {
            setDebouncedFiltros({
                nome: filtroNome,
                inscricao: filtroInscricao,
                cpf: filtroCpf,
                passaporte: filtroPassaporte,
                email: filtroEmail
            });
            setPaginaAtual(0);
            setDebouncing(false);
        }, 1500);
        
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [filtroNome, filtroInscricao, filtroCpf, filtroPassaporte, filtroEmail]);

    useEffect(() => {
        if (processoId) {
            fetchCandidatos();
        }
    }, [processoId, exibirDeletados, paginaAtual, tamanhoPagina, ordenacao, debouncedFiltros.nome, debouncedFiltros.inscricao, debouncedFiltros.cpf, debouncedFiltros.passaporte, debouncedFiltros.email, filtroStatus]);

    useEffect(() => {
        if (processoId) {
            fetchProcesso();
        }
    }, [processoId]);
    
    // Status e exibirDeletados aplicam imediatamente
    useEffect(() => {
        setPaginaAtual(0);
    }, [filtroStatus, exibirDeletados]);

    const handleLimparFiltros = () => {
        setFiltroNome('');
        setFiltroInscricao('');
        setFiltroCpf('');
        setFiltroPassaporte('');
        setFiltroEmail('');
        setFiltroStatus('TODOS');
        setPaginaAtual(0);
    };

    const handleOrdenacao = (campo: string) => {
        if (ordenacao === campo) {
            setOrdenacao(`${campo},desc`);
        } else {
            setOrdenacao(campo);
        }
        setPaginaAtual(0);
    };

    const irParaPagina = (novaPagina: number) => {
        setPaginaAtual(novaPagina);
    };

    const handleShowResponsavelModal = (responsavel: CandidatoResponsavel | null) => {
        setResponsavelSelecionado(responsavel);
        setShowResponsavelModal(true);
    };

    const handleCloseResponsavelModal = () => {
        setShowResponsavelModal(false);
        setResponsavelSelecionado(null);
    };

    const renderHeaderOrdenavel = (campo: string, label: string) => {
        const isAtivo = ordenacao.startsWith(campo);
        const isDesc = ordenacao.endsWith('desc');
        
        return (
            <th 
                onClick={() => handleOrdenacao(campo)}
                style={{ cursor: 'pointer' }}
            >
                {label}
                {isAtivo && (
                    <span className="ml-1">
                        {isDesc ? ' ↓' : ' ↑'}
                    </span>
                )}
            </th>
        );
    };

    const renderPaginacao = () => {
        if (!paginationData || paginationData.totalPages <= 1) return null;

        const { number: currentPage, totalPages, size } = paginationData;
        const startItem = currentPage * size + 1;
        const endItem = Math.min((currentPage + 1) * size, paginationData.totalElements);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Mostrando {startItem} a {endItem} de {paginationData.totalElements} itens
                </div>
                
                <Pagination>
                    <Pagination.First 
                        onClick={() => setPaginaAtual(0)} 
                        disabled={currentPage === 0} 
                    />
                    <Pagination.Prev 
                        onClick={() => setPaginaAtual(currentPage - 1)} 
                        disabled={currentPage === 0} 
                    />
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber: number;
                        if (totalPages <= 5) {
                            pageNumber = i;
                        } else if (currentPage < 2) {
                            pageNumber = i;
                        } else if (currentPage > totalPages - 3) {
                            pageNumber = totalPages - 5 + i;
                        } else {
                            pageNumber = currentPage - 2 + i;
                        }
                        
                        return (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => setPaginaAtual(pageNumber)}
                            >
                                {pageNumber + 1}
                            </Pagination.Item>
                        );
                    })}
                    
                    <Pagination.Next 
                        onClick={() => setPaginaAtual(currentPage + 1)} 
                        disabled={currentPage >= totalPages - 1} 
                    />
                    <Pagination.Last 
                        onClick={() => setPaginaAtual(totalPages - 1)} 
                        disabled={currentPage >= totalPages - 1} 
                    />
                </Pagination>
                
                <Form.Select
                    value={tamanhoPagina}
                    onChange={(e) => {
                        setTamanhoPagina(Number(e.target.value));
                        setPaginaAtual(0);
                    }}
                    style={{ width: '80px' }}
                >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </Form.Select>
            </div>
        );
    };

    const renderEstatisticas = () => {
        return (
            <Row className="mb-4">
                <Col md={2}>
                    <Card bg="light" text="dark" className="text-center">
                        <Card.Body>
                            <Card.Title>Total</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas.TOTAL || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card bg="light" text="success" className="text-center">
                        <Card.Body>
                            <Card.Title>Validados</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas[StatusCandidato.VALIDADO] || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card bg="light" text="danger" className="text-center">
                        <Card.Body>
                            <Card.Title>Não Validados</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas[StatusCandidato.NAO_VALIDADO] || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card bg="light" text="warning" className="text-center">
                        <Card.Body>
                            <Card.Title>Inconclusivos</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas[StatusCandidato.INCONCLUSIVO] || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card bg="light" text="secondary" className="text-center">
                        <Card.Body>
                            <Card.Title>Ausentes</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas[StatusCandidato.AUSENTE] || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card bg="light" text="info" className="text-center">
                        <Card.Body>
                            <Card.Title>Não avaliados</Card.Title>
                            <Card.Text as="h4">{estatisticasEstaticas[StatusCandidato.NAO_AVALIADO] || 0}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    return (
        <Container fluid className="mt-4">
            <ToastContainer position="top-right" autoClose={5000} />
            
            <Row className="mb-4">
                <Col>
                    <h2>Candidatos do Processo</h2>
                    <Badge bg="secondary">Processo: {processo?.nome} ({processo?.identificador})</Badge>
                </Col>
            </Row>

            {/* Estatísticas */}
            {!loading && !error && candidatos.length > 0 && renderEstatisticas()}

            <Row className="mb-4">
                <Col>
                    <Form>
                        <Row className="mb-3">
                            <Col md={2}>
                                <Form.Group controlId="filtroNome">
                                    <Form.Label>Nome</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filtrar por nome"
                                            value={filtroNome}
                                            onChange={(e) => setFiltroNome(e.target.value)}
                                        />
                                        {debouncing && filtroNome && (
                                            <InputGroup.Text>
                                                <Spinner size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="filtroInscricao">
                                    <Form.Label>Inscrição</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filtrar por inscrição"
                                            value={filtroInscricao}
                                            onChange={(e) => setFiltroInscricao(e.target.value)}
                                        />
                                        {debouncing && filtroInscricao && (
                                            <InputGroup.Text>
                                                <Spinner size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="filtroCpf">
                                    <Form.Label>CPF</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filtrar por CPF"
                                            value={filtroCpf}
                                            onChange={(e) => setFiltroCpf(e.target.value)}
                                        />
                                        {debouncing && filtroCpf && (
                                            <InputGroup.Text>
                                                <Spinner size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="filtroPassaporte">
                                    <Form.Label>Passaporte</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filtrar por passaporte"
                                            value={filtroPassaporte}
                                            onChange={(e) => setFiltroPassaporte(e.target.value)}
                                        />
                                        {debouncing && filtroPassaporte && (
                                            <InputGroup.Text>
                                                <Spinner size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="filtroEmail">
                                    <Form.Label>Email</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filtrar por email"
                                            value={filtroEmail}
                                            onChange={(e) => setFiltroEmail(e.target.value)}
                                        />
                                        {debouncing && filtroEmail && (
                                            <InputGroup.Text>
                                                <Spinner size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="filtroStatus">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={filtroStatus}
                                        onChange={(e) => setFiltroStatus(e.target.value as StatusCandidato | 'TODOS')}
                                    >
                                        <option value="TODOS">Todos</option>
                                        {Object.values(StatusCandidato).map((status) => (
                                            <option key={status} value={status}>
                                                {getStatusLabel(status)}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={2}>
                                <Form.Group controlId="exibirDeletados">
                                    <Form.Label>Exibir Deletados</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        checked={exibirDeletados}
                                        onChange={(e) => setExibirDeletados(e.target.checked)}
                                        label={exibirDeletados ? 'Sim' : 'Não'}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={10} className="d-flex align-items-end justify-content-end">
                                <Button
                                    variant="secondary"
                                    onClick={handleLimparFiltros}
                                >
                                    <BsArrowCounterclockwise /> Limpar Filtros
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>

            <Row>
                <Col>
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </Spinner>
                            <p>Carregando candidatos...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : candidatos.length === 0 ? (
                        <div className="alert alert-info">Nenhum candidato encontrado.</div>
                    ) : (
                        <>
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        {renderHeaderOrdenavel('inscricao', 'Inscrição')}
                                        {renderHeaderOrdenavel('nome', 'Nome')}
                                        <th>Nome Social</th>
                                        <th>CPF/Passaporte</th>
                                        <th>E-mail</th>
                                        <th>Foto</th>
                                        <th>Status</th>
                                        <th>Validação anterior</th>
                                        <th>Responsável</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidatos.map((candidato) => (
                                        <tr 
                                            key={candidato.id}
                                            style={{ backgroundColor: statusColors[candidato.status as StatusCandidato] || '#fff' }}
                                        >
                                            {/* ALTERAÇÃO: Usar a nova função para renderizar inscrições */}
                                            <td>{renderInscricoes(candidato.candidatoEditais)}</td>
                                            <td>{candidato.nome}</td>
                                            <td>{candidato.nomeSocial || '-'}</td>
                                            <td>{candidato.cpf || candidato.passaporte || '-'}</td>
                                            <td>{candidato.email || '-'}</td>
                                            <td className="text-center">{candidato.temFoto ? 'Sim' : 'Não'}</td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    {getStatusIcon(candidato.status)}
                                                    <span className="ms-2">{getStatusLabel(candidato.status)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {candidato.status === StatusCandidato.VALIDADO_ANTERIORMENTE && candidato.validacaoAnterior ? (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`validacao-anterior-${candidato.id}`}>
                                                                Processo: {candidato.validacaoAnterior.processoNome} ({candidato.validacaoAnterior.processoIdentificador})<br />
                                                                Edital: {candidato.validacaoAnterior.edital || '-'}<br />
                                                                Banca: {candidato.validacaoAnterior.tipoBanca || '-'}<br />
                                                                Votação: {candidato.validacaoAnterior.statusVotacao || '-'}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Badge bg="primary" className="cursor-pointer">
                                                            Ver origem
                                                        </Badge>
                                                    </OverlayTrigger>
                                                ) : '-'}
                                            </td>
                                            <td>
                                                {candidato.candidatoResponsavel ? (
                                                    <Button 
                                                        variant="link" 
                                                        onClick={() => handleShowResponsavelModal(candidato.candidatoResponsavel)}
                                                        className="p-0"
                                                    >
                                                        <BsInfoCircle className="me-1" />
                                                        {candidato.candidatoResponsavel.nome}
                                                    </Button>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {renderPaginacao()}
                        </>
                    )}
                </Col>
            </Row>

            {/* Modal de Responsável */}
            <Modal show={showResponsavelModal} onHide={handleCloseResponsavelModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Informações do Responsável</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {responsavelSelecionado ? (
                        <div>
                            <p><strong>Nome:</strong> {responsavelSelecionado.nome}</p>
                            {responsavelSelecionado.nomeSocial && (
                                <p><strong>Nome Social:</strong> {responsavelSelecionado.nomeSocial}</p>
                            )}
                            <p><strong>Documento:</strong> {responsavelSelecionado.cpf || responsavelSelecionado.passaporte || 'Não informado'}</p>
                            <p><strong>E-mail:</strong> {responsavelSelecionado.email || 'Não informado'}</p>
                        </div>
                    ) : (
                        <p>Nenhum responsável selecionado</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseResponsavelModal}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CandidatosProcesso;
